import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { TileDeFato } from "../componentes/CalendarioUi";
import { Cabine } from "../componentes/FerramentaUi";
import { TarefaDialogo } from "../componentes/PeritoDialogos";
import { Alternador, Chip } from "../componentes/PeritoUi";
import { diasAte, formatarData } from "../lib/perito/datas";
import { PRIORIDADE_LABEL, PRIORIDADE_TOM, TAREFA_LABEL } from "../lib/perito/format";
import { usePerito } from "../lib/perito/store";
import type { Tarefa } from "../lib/perito/tipos";
import { nomeDaPericia } from "./PeritoPericias";

// As tarefas de todas as perícias (fase B), portadas de `src/routes/_authenticated/tarefas.tsx`. A caixinha
// conclui e reabre (a data da conclusão sai quando a tarefa reabre: a RPC apaga e a tabela tem a constraint,
// 000385), e o filtro fica no endereço (`?mostrar=concluidas`).
//
// O que mudou de lá: excluir saiu da LINHA. Lá a lixeira apagava na hora, sem pergunta, ao lado do lápis de
// editar; aqui excluir mora no diálogo da tarefa, como na ficha da perícia.

const MOSTRAR = [
  { id: "abertas", rotulo: "Em aberto" },
  { id: "concluidas", rotulo: "Concluídas" },
  { id: "todas", rotulo: "Todas" },
] as const;
type Mostrar = (typeof MOSTRAR)[number]["id"];
const RAIZ = "/ferramenta/central-do-perito";

/**
 * A ordem da lista: as abertas pelo prazo (as sem prazo no fim), e as concluídas da mais recente. Lá era a
 * ordem de criação, e a tarefa que vence amanhã podia estar no fim de uma lista longa.
 */
function ordenar(a: Tarefa, b: Tarefa): number {
  const fa = a.status === "concluida" ? 1 : 0;
  const fb = b.status === "concluida" ? 1 : 0;
  if (fa !== fb) return fa - fb;
  if (fa === 1) return (b.concluidoEm ?? "").localeCompare(a.concluidoEm ?? "") || a.id.localeCompare(b.id);
  return (a.prazo || "9999").localeCompare(b.prazo || "9999") || a.titulo.localeCompare(b.titulo);
}

export default function PeritoTarefas() {
  const { tarefas, pericias, updateTarefa, podeEscrever } = usePerito();
  const [query, setQuery] = useSearchParams();
  const [editando, setEditando] = useState<Tarefa | null>(null);
  const [criando, setCriando] = useState(false);
  const mostrar: Mostrar = (MOSTRAR.find((m) => m.id === query.get("mostrar"))?.id ?? "abertas") as Mostrar;

  const numeros = useMemo(() => {
    const abertas = tarefas.filter((t) => t.status !== "concluida");
    return { abertas: abertas.length, atrasadas: abertas.filter((t) => (diasAte(t.prazo) ?? 0) < 0).length, concluidas: tarefas.length - abertas.length };
  }, [tarefas]);

  const lista = useMemo(
    () => tarefas.filter((t) => (mostrar === "todas" ? true : mostrar === "concluidas" ? t.status === "concluida" : t.status !== "concluida")).sort(ordenar),
    [tarefas, mostrar],
  );

  return (
    <div className="flex flex-col gap-5" data-tarefas>
      <Cabine rotulo="Trabalho" titulo="Tarefas"
        descricao="As tarefas de cada etapa nascem sozinhas quando a perícia é cadastrada e quando muda de etapa. As suas entram em Nova tarefa."
        acao={podeEscrever ? <button type="button" onClick={() => setCriando(true)} className="pill-cta inline-flex min-h-11 items-center px-5 text-sm" data-nova-tarefa>Nova tarefa</button> : undefined}
        fatos={tarefas.length > 0 ? (
          <>
            <TileDeFato rotulo="Em aberto" valor={<span data-fato="abertas">{numeros.abertas}</span>} apoio="pendentes ou em andamento" />
            {numeros.atrasadas > 0 && <TileDeFato rotulo="Atrasadas" valor={<span data-fato="atrasadas">{numeros.atrasadas}</span>} apoio="com o prazo vencido" />}
            {numeros.concluidas > 0 && <TileDeFato rotulo="Concluídas" valor={<span data-fato="concluidas">{numeros.concluidas}</span>} apoio="até agora" />}
          </>
        ) : undefined} />

      {tarefas.length > 0 && (
        <div>
          <Alternador rotulo="Mostrar" opcoes={MOSTRAR} valor={mostrar}
            aoMudar={(m) => { const q = new URLSearchParams(query); if (m === "abertas") q.delete("mostrar"); else q.set("mostrar", m); setQuery(q, { replace: true }); }} />
        </div>
      )}

      {tarefas.length === 0 ? (
        <p className="surface p-6 text-sm text-cinza">Nenhuma tarefa ainda. As tarefas de cada etapa são criadas quando uma perícia é cadastrada, e você pode criar as suas em Nova tarefa.</p>
      ) : lista.length === 0 ? (
        <p className="py-6 text-center text-sm text-cinza">Nenhuma tarefa neste filtro.</p>
      ) : (
        <ul className="flex flex-col gap-2.5" data-lista-de-tarefas>
          {lista.map((t) => {
            const pericia = pericias.find((p) => p.id === t.pericia_id);
            const dias = diasAte(t.prazo);
            const concluida = t.status === "concluida";
            const atrasada = !concluida && dias !== null && dias < 0;
            const feitos = t.checklist.filter((c) => c.feito).length;
            return (
              <li key={t.id} className={`surface flex items-start gap-3 p-4 ${atrasada ? "border-l-4 border-l-alerta-tinta" : ""}`} data-tarefa={t.id} data-atrasada={atrasada || undefined}>
                <input type="checkbox" className="mt-1 size-4 accent-acento" checked={concluida} disabled={!podeEscrever}
                  onChange={(e) => updateTarefa(t.id, { status: e.target.checked ? "concluida" : "pendente" })}
                  aria-label={`${concluida ? "Reabrir" : "Concluir"} a tarefa ${t.titulo}`} data-concluir />
                <div className="min-w-0 flex-1">
                  <p className={`font-semibold ${concluida ? "text-cinza line-through" : "text-titulo"}`}>{t.titulo}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-cinza">
                    {pericia && <Link to={`${RAIZ}/pericias/${pericia.id}?aba=tarefas`} className="font-semibold text-link hover:underline">{nomeDaPericia(pericia)}</Link>}
                    {t.prazo && <span className={`tabular-nums ${atrasada ? "font-semibold text-alerta-tinta" : ""}`}>Prazo {formatarData(t.prazo)}</span>}
                    {concluida && t.concluidoEm ? <span className="tabular-nums">Concluída em {formatarData(t.concluidoEm)}</span> : <span>{TAREFA_LABEL[t.status]}</span>}
                    {t.checklist.length > 0 && <Chip tom={feitos === t.checklist.length ? "ok" : "neutro"}>{feitos}/{t.checklist.length} itens</Chip>}
                  </div>
                  {t.prazoSugerido && !concluida && podeEscrever && (
                    <button type="button" onClick={() => setEditando(t)} className="mt-1.5 text-left text-xs font-semibold text-alerta-tinta hover:underline">
                      Prazo sugerido pelo modelo. Ajuste ao prazo definido na sua perícia.
                    </button>
                  )}
                </div>
                <Chip tom={PRIORIDADE_TOM[t.prioridade]}>{PRIORIDADE_LABEL[t.prioridade]}</Chip>
                {podeEscrever && (
                  <button type="button" onClick={() => setEditando(t)} className="text-xs font-semibold text-link hover:underline" aria-label={`Editar a tarefa ${t.titulo}`} data-editar-tarefa>
                    Editar
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <TarefaDialogo tarefa={editando} aberto={!!editando || criando} aoFechar={() => { setEditando(null); setCriando(false); }} />
    </div>
  );
}
